import React, { useEffect, useState } from 'react';
import { getErrorMessage, formatCurrency, formatUnitPrice } from '@/lib/utils';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  Calendar,
  Package,
  Save,
  Search,
  Heart,
  MapPin,
  Building2,
  Truck,
  AlertCircle,
  Loader2,
  Info
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewOrderPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const relationshipId = searchParams.get('relationship');
  
  const [catalog, setCatalog] = useState(null);
  const [orderLines, setOrderLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [favorites, setFavorites] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  
  // Messaggio quantità minima (per ogni riga)
  const [minQtyWarnings, setMinQtyWarnings] = useState<Record<number, boolean>>({});
  
  // Siti di consegna
  const [deliverySites, setDeliverySites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [hasMultisito, setHasMultisito] = useState(false);
  
  // Costi di spedizione
  const [deliveryCost, setDeliveryCost] = useState(null);
  const [loadingDeliveryCost, setLoadingDeliveryCost] = useState(false);

  useEffect(() => {
    if (relationshipId) {
      fetchCatalog();
      fetchFavorites();
      fetchDeliverySites();
    }
  }, [relationshipId]);

  const fetchDeliverySites = async () => {
    try {
      const res = await axios.get(`${API}/delivery-sites`);
      setDeliverySites(res.data.sites || []);
      setHasMultisito(res.data.has_multisito || false);
      
      // Seleziona automaticamente il sito default
      const defaultSite = (res.data.sites || []).find(s => s.is_default);
      if (defaultSite) {
        setSelectedSiteId(defaultSite.id);
      } else if (res.data.sites?.length > 0) {
        setSelectedSiteId(res.data.sites[0].id);
      }
    } catch (error) {
      console.error('Error fetching delivery sites:', error);
    }
  };

  // Calcola costi di spedizione quando cambia sito o totale
  const fetchDeliveryCost = async (sellerId) => {
    if (!sellerId) return;
    
    const selectedSite = deliverySites.find(s => s.id === selectedSiteId);
    if (!selectedSite && deliverySites.length > 0) return;
    
    setLoadingDeliveryCost(true);
    try {
      const params = new URLSearchParams();
      if (selectedSite) {
        params.append('postal_code', selectedSite.postal_code);
        params.append('province', selectedSite.province);
      }
      params.append('order_total', calculateTotal().toString());
      
      const res = await axios.get(`${API}/delivery-zones/calculate/${sellerId}?${params.toString()}`);
      setDeliveryCost(res.data);
    } catch (error) {
      console.error('Error fetching delivery cost:', error);
      setDeliveryCost(null);
    } finally {
      setLoadingDeliveryCost(false);
    }
  };

  // Aggiorna costi spedizione quando cambiano sito, totale o catalogo
  useEffect(() => {
    if (catalog?.seller_id && deliverySites.length > 0) {
      const timer = setTimeout(() => {
        fetchDeliveryCost(catalog.seller_id);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedSiteId, orderLines, catalog?.seller_id, deliverySites]);

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites/by-seller/${catalog?.products?.[0]?.seller_id || ''}?relationship_id=${relationshipId}`);
      // Will be called after catalog loads, use seller from catalog
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  // Fetch favorites when catalog is loaded
  useEffect(() => {
    if (catalog?.products?.length > 0 && relationshipId) {
      const sellerId = catalog.products[0].seller_id;
      axios.get(`${API}/favorites/by-seller/${sellerId}`)
        .then(res => {
          setFavorites(new Set(res.data.product_ids || []));
        })
        .catch(err => console.error('Error fetching favorites:', err));
    }
  }, [catalog, relationshipId]);

  const fetchCatalog = async () => {
    try {
      const params = new URLSearchParams();
      if (productSearch) params.append('search', productSearch);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const res = await axios.get(`${API}/buyer/catalog/${relationshipId}?${params.toString()}`);
      setCatalog(res.data);
    } catch (error) {
      console.error('Error fetching catalog:', error);
      if (!catalog) {
        toast.error('Errore nel caricamento del catalogo');
        navigate('/orders');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (catalog) {
      // Re-fetch when search/filter changes (debounced)
      const timer = setTimeout(() => {
        fetchCatalog();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [productSearch, categoryFilter]);

  const addProduct = () => {
    if (!selectedProduct) return;
    
    const product = catalog.products.find(p => p.id === selectedProduct);
    if (!product) return;
    
    const existingIndex = orderLines.findIndex(l => l.product_id === product.id);
    if (existingIndex >= 0) {
      const newLines = [...orderLines];
      newLines[existingIndex].quantity += 1;
      setOrderLines(newLines);
    } else {
      setOrderLines([...orderLines, {
        product_id: product.id,
        product_name: product.name,
        quantity: product.min_quantity || 1,
        unit_price: product.price,
        unit_of_measure: product.unit_of_measure
      }]);
    }
    setSelectedProduct('');
  };

  const updateQuantity = (index: number, delta: number) => {
    const newLines = [...orderLines];
    const product = catalog.products.find((p: any) => p.id === newLines[index].product_id);
    const minQty = product?.min_quantity || 1;
    const currentQty = newLines[index].quantity;
    const newQty = currentQty + delta;
    
    // Se sta cercando di scendere sotto il minimo
    if (delta < 0 && newQty < minQty) {
      // Mostra il warning per questa riga
      setMinQtyWarnings(prev => ({ ...prev, [index]: true }));
      // Nascondi dopo 3 secondi
      setTimeout(() => {
        setMinQtyWarnings(prev => ({ ...prev, [index]: false }));
      }, 3000);
      return; // Non aggiornare la quantità
    }
    
    // Rimuovi warning se aumenta
    if (delta > 0 && minQtyWarnings[index]) {
      setMinQtyWarnings(prev => ({ ...prev, [index]: false }));
    }
    
    newLines[index].quantity = Math.max(minQty, newQty);
    setOrderLines(newLines);
  };

  const removeLine = (index) => {
    setOrderLines(orderLines.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return orderLines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateTotal();
    const shippingCost = deliveryCost?.total_cost || 0;
    return subtotal + shippingCost;
  };

  const handleSaveOrder = async (submit = false) => {
    if (orderLines.length === 0) {
      toast.error('Aggiungi almeno un prodotto');
      return;
    }

    setSaving(true);
    try {
      const orderData = {
        relationship_id: relationshipId,
        lines: orderLines,
        notes,
        delivery_date: deliveryDate || null,
        delivery_site_id: selectedSiteId || null,
        delivery_cost: deliveryCost ? {
          zone_id: deliveryCost.zone_id,
          zone_name: deliveryCost.zone_name,
          shipping_cost: deliveryCost.total_cost,
          is_free: deliveryCost.is_free
        } : null
      };

      const res = await axios.post(`${API}/orders`, orderData);
      
      if (submit) {
        await axios.post(`${API}/orders/${res.data.id}/submit`);
        toast.success('Ordine inviato con successo');
      } else {
        toast.success('Ordine salvato come bozza');
      }
      
      navigate('/orders');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const toggleFavorite = async (productId) => {
    try {
      if (favorites.has(productId)) {
        await axios.delete(`${API}/favorites/${productId}?relationship_id=${relationshipId}`);
        setFavorites(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        toast.success('Rimosso dai preferiti');
      } else {
        await axios.post(`${API}/favorites/${productId}?relationship_id=${relationshipId}`);
        setFavorites(prev => new Set([...prev, productId]));
        toast.success('Aggiunto ai preferiti');
      }
    } catch (error) {
      toast.error('Errore nella gestione dei preferiti');
    }
  };

  const addProductById = (productId) => {
    const product = catalog.products.find(p => p.id === productId);
    if (!product) return;
    
    const existingIndex = orderLines.findIndex(l => l.product_id === product.id);
    if (existingIndex >= 0) {
      const newLines = [...orderLines];
      newLines[existingIndex].quantity += 1;
      setOrderLines(newLines);
    } else {
      setOrderLines([...orderLines, {
        product_id: product.id,
        product_name: product.name,
        quantity: product.min_quantity || 1,
        unit_price: product.price,
        unit_of_measure: product.unit_of_measure
      }]);
    }
    toast.success(`${product.name} aggiunto`);
  };

  // Filter products based on active tab
  const getFilteredProducts = () => {
    if (!catalog?.products) return [];
    if (activeTab === 'favorites') {
      return catalog.products.filter(p => favorites.has(p.id));
    }
    return catalog.products;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Catalogo non disponibile</p>
        <Button onClick={() => navigate('/orders')} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    );
  }

  // Caso: catalogo caricato ma senza prodotti
  if (catalog && (!catalog.products || catalog.products.length === 0)) {
    return (
      <div className="space-y-6" data-testid="new-order-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold">{t('orders.newOrder')}</h1>
          </div>
        </div>
        
        <Card>
          <CardContent className="py-16 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Catalogo vuoto</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Questo fornitore non ha ancora pubblicato prodotti nel catalogo a te associato. 
              Contattalo per richiedere l&apos;aggiornamento del listino.
            </p>
            <Button onClick={() => navigate('/orders')} variant="outline" className="mt-6">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Torna agli Ordini
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="new-order-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold">{t('orders.newOrder')}</h1>
          <p className="text-muted-foreground">
            Listino: {catalog.pricelist_name}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Product Selection */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t('orders.selectProduct')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tabs: All / Favorites */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="all" data-testid="tab-all-products">
                  Tutti i Prodotti
                </TabsTrigger>
                <TabsTrigger value="favorites" data-testid="tab-favorites" className="flex items-center gap-2">
                  <span className="text-red-500">♥</span> Preferiti ({favorites.size})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="space-y-4">
                {/* Search and Filter */}
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Cerca prodotti..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="pl-10"
                      data-testid="product-search-input"
                    />
                  </div>
                  {catalog?.categories?.length > 0 && (
                    <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Tutte le categorie" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tutte le categorie</SelectItem>
                        {catalog.categories.filter(cat => cat && cat.trim() !== '').map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Product Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
                  {getFilteredProducts().map((product) => (
                    <div 
                      key={product.id} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`product-card-${product.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatUnitPrice(product.price, product.unit_of_measure || 'pz')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <button
                          onClick={() => toggleFavorite(product.id)}
                          className="p-2 hover:bg-muted rounded-full transition-colors"
                          data-testid={`favorite-btn-${product.id}`}
                        >
                          <span 
                            className={`text-xl transition-all ${favorites.has(product.id) ? 'text-red-500' : 'text-gray-300 hover:text-red-300'}`}
                          >
                            ♥
                          </span>
                        </button>
                        <Button 
                          size="sm" 
                          onClick={() => addProductById(product.id)}
                          data-testid={`add-btn-${product.id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="favorites" className="space-y-4">
                {favorites.size === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <span className="text-4xl block mb-2">♥</span>
                    <p>Nessun prodotto nei preferiti</p>
                    <p className="text-sm">Clicca sul cuore per aggiungere prodotti</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
                    {getFilteredProducts().map((product) => (
                      <div 
                        key={product.id} 
                        className="flex items-center justify-between p-3 border border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900 rounded-lg hover:bg-red-100/50 dark:hover:bg-red-950/40 transition-colors"
                        data-testid={`favorite-product-${product.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{product.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatUnitPrice(product.price, product.unit_of_measure || 'pz')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <button
                            onClick={() => toggleFavorite(product.id)}
                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors"
                          >
                            <span className="text-xl text-red-500">♥</span>
                          </button>
                          <Button 
                            size="sm" 
                            onClick={() => addProductById(product.id)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Order Lines */}
            {orderLines.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase text-xs">Prodotto</TableHead>
                    <TableHead className="uppercase text-xs text-center">Qtà</TableHead>
                    <TableHead className="uppercase text-xs text-right">Prezzo</TableHead>
                    <TableHead className="uppercase text-xs text-right">Totale</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orderLines.map((line, index) => {
                    const product = catalog?.products?.find((p: any) => p.id === line.product_id);
                    const minQty = product?.min_quantity || 1;
                    const showWarning = minQtyWarnings[index];
                    
                    return (
                    <TableRow key={index} data-testid={`order-line-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{line.product_name}</p>
                          <p className="text-xs text-muted-foreground">{line.unit_of_measure}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => updateQuantity(index, -1)}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-12 text-center font-mono">{line.quantity}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => updateQuantity(index, 1)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          {/* Messaggio quantità minima */}
                          {showWarning && minQty > 1 && (
                            <p className="text-xs text-amber-600 flex items-center gap-1 animate-in fade-in duration-200">
                              <Info className="h-3 w-3" />
                              Quantità minima: {minQty}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(line.unit_price)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(line.quantity * line.unit_price)}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => removeLine(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Seleziona i prodotti da ordinare</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Order Summary */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Riepilogo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Subtotale prodotti */}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotale prodotti</span>
                <span data-testid="order-subtotal">
                  {formatCurrency(calculateTotal())}
                </span>
              </div>

              {/* Costi di spedizione */}
              {deliverySites.length > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    Spedizione
                    {deliveryCost?.zone_name && (
                      <span className="text-xs">({deliveryCost.zone_name})</span>
                    )}
                  </span>
                  {loadingDeliveryCost ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : deliveryCost?.is_free ? (
                    <Badge variant="secondary" className="bg-green-100 text-green-700">Gratuita</Badge>
                  ) : (
                    <span data-testid="shipping-cost">
                      {formatCurrency(deliveryCost?.total_cost || 0)}
                    </span>
                  )}
                </div>
              )}

              {/* Mancante per spedizione gratuita */}
              {deliveryCost?.amount_for_free && deliveryCost.amount_for_free > 0 && (
                <div className="text-xs text-center p-2 bg-amber-50 dark:bg-amber-950/30 rounded text-amber-700 dark:text-amber-400">
                  Aggiungi {formatCurrency(deliveryCost.amount_for_free)} per la spedizione gratuita
                </div>
              )}

              <div className="border-t pt-3">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Totale</span>
                  <span className="text-primary" data-testid="order-total">
                    {formatCurrency(calculateGrandTotal())}
                  </span>
                </div>
              </div>

              {/* Selezione Sito di Consegna */}
              {deliverySites.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Sito di Consegna
                  </Label>
                  {deliverySites.length === 1 ? (
                    <div className="p-3 rounded-lg bg-muted/50 border">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium text-sm">{deliverySites[0].name}</div>
                          <div className="text-xs text-muted-foreground">
                            {deliverySites[0].address}, {deliverySites[0].city}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                      <SelectTrigger data-testid="delivery-site-select">
                        <SelectValue placeholder="Seleziona sito..." />
                      </SelectTrigger>
                      <SelectContent>
                        {deliverySites.map(site => (
                          <SelectItem key={site.id} value={site.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{site.name}</span>
                              <span className="text-xs text-muted-foreground">
                                {site.address}, {site.city}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* Mostra info sito selezionato */}
                  {selectedSiteId && deliverySites.length > 1 && (
                    <div className="text-xs text-muted-foreground p-2 bg-muted/30 rounded">
                      {(() => {
                        const site = deliverySites.find(s => s.id === selectedSiteId);
                        return site ? (
                          <>
                            <div>{site.address}, {site.postal_code} {site.city}</div>
                            {site.contact_name && <div>Ref: {site.contact_name} {site.contact_phone && `• ${site.contact_phone}`}</div>}
                          </>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="delivery-date">Data Consegna</Label>
                <Input
                  id="delivery-date"
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  data-testid="delivery-date"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Note aggiuntive..."
                  rows={3}
                  data-testid="order-notes"
                />
              </div>

              <div className="space-y-2 pt-4">
                <Button 
                  className="w-full"
                  onClick={() => handleSaveOrder(true)}
                  disabled={saving || orderLines.length === 0}
                  data-testid="submit-order-btn"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  {saving ? t('common.loading') : t('orders.submitOrder')}
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => handleSaveOrder(false)}
                  disabled={saving || orderLines.length === 0}
                  data-testid="save-draft-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Salva Bozza
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NewOrderPage;
